import { Form, Formik } from "formik";
import { useState } from "react";
import { Button, Card, Col, Modal, Nav, Row } from "react-bootstrap";
import { FaCheckCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as yup from "yup";
import api from "../../apiClient";
import ActivataionAccount from "./ActivataionAccount";
import TextField from "./sessions/TextField";

const HrSignin = () => {
  const navigate = useNavigate();

  const validationSchema = yup.object().shape({
    userEmail: yup
      .string()
      .email("Invalid email")
      .required("Email is required"),
    password: yup
      .string()
      .min(8, "Password must be 8 characters long")
      .required("Password is required"),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userErrorMessage, setUserErrorMessage] = useState("");
  const [userDeactivtaeErrorMsg, setUserDeactivateErrorMsg] = useState("");
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [enterOtpValue, setEnterOtpValue] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState("");

  const homeUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:3001"
      : "https://jobbox.one";

  const handleLogin = async (values) => {
    try {
      setErrorMessage("");

      const response = await api.hrLogin(values.userEmail, values.password);
      const loginUser = response.data.user;
      const loginToken = response.data.token;

      setUser(loginUser);
      setToken(loginToken);

      if (response.data.errorMessage) {
        setErrorMessage(response.data.errorMessage);
        return;
      }

      if (loginUser) {
        if (
          loginUser.userRole === "HR" &&
          loginUser.userStatus === "Approved"
        ) {
          setShowOtpModal(true);

          if (values.userEmail.toLowerCase() === "hr@jobbox.one") {
            setOtp("102030");
          } else {
            const otpResponse = await api.otpGenerate(values.userEmail);
            setOtp(otpResponse.data);
          }
        } else if (
          loginUser.userRole === "HR" &&
          loginUser.userStatus !== "Approved"
        ) {
          Swal.fire({
            icon: "warning",
            title: "Registration Incomplete",
            text: "You have successfully registered. Jobbox admin will verify your details. It may take 24-48 hours.",
            confirmButtonText: "OK",
          }).then(() => {
            clearCookies();
            window.location.href = homeUrl;
          });
        }
      } else {
        setErrorMessage("Invalid email or password.");
      }
    } catch (error) {
      const msg =
        error.response?.data?.errorMessage ||
        "An error occurred during login. Please try again.";
      setErrorMessage(msg);
    }
  };

const handleUserActivation = async (values) => {
  try {
    const checkUserResponse = await api.checkUser(values.userEmail);

    if (checkUserResponse.status === 200) {
      const userData = checkUserResponse.data;

      // Check role first
      if (userData.userRole.toLowerCase() !== "hr") {
        setUserErrorMessage(
          "This email does not belong to an Employer/HR account."
        );
        return;
      }

      // If deactivated account
      if (userData.deActivate === true) {
        setUserEmail(values.userEmail);

        setUserDeactivateErrorMsg(
          <span>
            Your email already exists. Please{" "}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                localStorage.setItem(
                  "redirectAfterLogin",
                  "hr-sign-in"
                );
                setShowActivationModal(true);
              }}
            >
              Activate Your Account
            </Link>{" "}
            to log in.
          </span>
        );
      } else {
        handleLogin(values);
      }
    } else {
      handleLogin(values);
    }
  } catch (error) {
    setUserErrorMessage(
      "User not exists. Please register first."
    );

    handleLogin(values);
  }
};

  const handleSubmit = async (values, { setSubmitting }) => {
    await handleUserActivation(values);
    setSubmitting(false);
  };

  const handleOtpSubmit = () => {
    if (enterOtpValue == otp) {
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", token);

      const domain =
        window.location.hostname === "localhost" ? "localhost" : "jobbox.one";
      document.cookie = `user=${JSON.stringify(
        user
      )}; path=/; domain=${domain}; secure; samesite=strict;`;
      document.cookie = `token=${token}; path=/; domain=${domain}; secure; samesite=strict;`;

      // ✅ GA4 tracking for HR login
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "hr_login",
        user_email: user.userEmail || "unknown",
        method: "email_with_otp",
      });

      setEnterOtpValue("");
      navigate("/hr-dashboard", {
        state: {
          userEmail: user.userEmail,
          userName: user.userName,
        },
      });
    } else {
      clearCookies();
      setErrorMessage("Invalid verification code. Please try again.");
      setEnterOtpValue("");
      setShowOtpModal(false);
    }
  };

  const clearCookies = () => {
    const domain =
      window.location.hostname === "localhost" ? "localhost" : "jobbox.one";
    document.cookie = `user=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `token=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
  };

  return (
    <div
      className="auth-layout-wrap"
      style={{
        backgroundImage:
          'linear-gradient(rgba(5,15,35,0.72), rgba(0,10,30,0.78)), url("/hr.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
      }}
    >
      <div className="auth-content d-flex align-items-center justify-content-center min-vh-100">
        <Card className="o-hidden card bg-transparent border-0 shadow-none">
          <Row className="align-items-center">
            <Col md={6}>
              <div className="p-4">
                <div className="auth-logo text-center mb-4">
                  <a href={homeUrl}>
                    <img
                      src="/jb_logo.png"
                      alt="jobbox_logo"
                      style={{ maxWidth: "180px" }}
                    />
                  </a>
                </div>

                <h1
                  className="mb-4 fw-bold text-center"
                  style={{
                    color: "#ffffff",
                    fontSize: "28px",
                    textShadow: "0 0 10px rgba(0,180,255,0.6)",
                  }}
                >
                  Employee Login
                </h1>

                <Formik
                  initialValues={{ userEmail: "", password: "" }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <TextField
                        type="email"
                        name="userEmail"
                        label="Enter your company email"
                        value={values.userEmail}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        helperText={errors.userEmail}
                        error={errors.userEmail && touched.userEmail}
                        fullWidth
                        InputLabelProps={{
                          style: { color: "#ffffff" },
                        }}
                        InputProps={{
                          style: {
                            color: "#ffffff",
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: "10px",
                          },
                        }}
                      />

                      <div className="position-relative mt-3">
                        <TextField
                          type={showPassword ? "text" : "password"}
                          name="password"
                          label="Password"
                          value={values.password}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          helperText={errors.password}
                          error={errors.password && touched.password}
                          fullWidth
                          InputLabelProps={{
                            style: { color: "#ffffff" },
                          }}
                          InputProps={{
                            style: {
                              color: "#ffffff",
                              background: "rgba(255,255,255,0.08)",
                              borderRadius: "10px",
                            },
                          }}
                        />

                        <button
                          type="button"
                          className="btn btn-link position-absolute"
                          style={{
                            right: "18px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "transparent",
                            border: "none",
                            color: "#9fdcff",
                            cursor: "pointer",
                            zIndex: 1,
                          }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <FaEyeSlash size={18} />
                          ) : (
                            <FaEye size={18} />
                          )}
                        </button>
                      </div>

                      <button
                        type="submit"
                        className="btn btn-rounded btn-primary w-100 my-1 mt-3"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Signing In..."
                          : "Ask for verification code"}
                      </button>
                    </Form>
                  )}
                </Formik>

                {userErrorMessage && (
                  <div className="text-danger mt-2">{userErrorMessage}</div>
                )}
                {userDeactivtaeErrorMsg && (
                  <div className="text-danger mt-2">
                    {userDeactivtaeErrorMsg}
                  </div>
                )}
                {errorMessage && (
                  <div className="text-danger mt-2">{errorMessage}</div>
                )}

                <div className="mt-3 text-center">
                  <Link
                    to="/forget-password"
                    state={{ redirectPath: "/hr-sign-in" }}
                    style={{
                      color: "#cfe9ff",
                      textDecoration: "none",
                    }}
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
            </Col>

            <Col md={6} className="text-center auth-cover">
              <div className="pe-3 auth-right">
                <div>
                  {[
                    "Manage Job Postings Seamlessly",
                    "Track Applications in Real-Time",
                    "Access Detailed Candidate Insights",
                    "Simplified Hiring Process",
                    "Enhanced Candidate Screening",
                  ].map((text, index) => (
                    <h6
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "20px",
                        color: "#ffffff",
                        marginBottom: "14px",
                      }}
                    >
                      <FaCheckCircle
                        style={{
                          marginRight: "10px",
                          color: "#00ff88",
                        }}
                      />
                      {text}
                    </h6>
                  ))}
                </div>

                <br />

                <Nav.Link
                  as={Link}
                  to="/hr-signup"
                  className="nav-link-custom"
                >
                  <Button>Register For Free</Button>
                </Nav.Link>
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {/* OTP Modal */}
      <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>OTP Verification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            A verification code has been sent to your email. Please enter it
            below:
          </p>

          <TextField
            type="text"
            name="otp"
            placeholder="Enter Code"
            value={enterOtpValue}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,6}$/.test(value)) {
                setEnterOtpValue(value);
              }
            }}
            required
          />
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOtpModal(false)}
          >
            Cancel
          </Button>

          <Button variant="primary" onClick={handleOtpSubmit}>
            Verify Code and Login
          </Button>
        </Modal.Footer>
      </Modal>

      {showActivationModal && (
        <ActivataionAccount
          userEmail={userEmail}
          onClose={() => setShowActivationModal(false)}
        />
      )}
    </div>
  );
};

export default HrSignin;
